<template>
  <BaseFlexSpinner v-if="refreshing" />
  <div v-else-if="program" class="flex flex-col p-6 gap-6">
    <div class="flex flex-row">
      <SupportProgramDescription v-if="program" :program="program" />
    </div>
    <div v-if="program.user_communication && !program.hook && !pendingPoll">
      <ProgramHook :hook="program.user_communication" />
    </div>
    <div v-if="program.sales && isHospitalProgram(program)">
      <HospitalProgramSalesCard
        :sales="program.sales"
        :start-at="dayjs().startOf('day').subtract(1, 'year').toDate()"
        :settings="program.programmable?.settings"
      />
    </div>
    <div v-if="pendingPoll">
      <ProgramHookPoll
        :poll="pendingPoll"
        :program="program"
        :consumer="pollConsumer"
        @answered="pollAnswered = true"
        @hidden="onPollHidden"
      />
    </div>
    <div v-else-if="program.hook">
      <ProgramHook :hook="program.hook" />
    </div>
    <div>
      <CustomerProgramOnboardingCommunication
        v-if="program?.onboarding_communication"
        :onboarding-communication="program.onboarding_communication"
        class="hidden sm:flex"
      />
      <div
        class="flex gap-2 gap-y-4 items-end flex-wrap-reverse justify-between"
      >
        <div>
          <CustomerProgramKnowledgeables
            v-if="program?.knowledgeables?.length"
            :knowledgeables="program.knowledgeables"
          />
        </div>
        <div class="flex flex-1 gap-2 items-center justify-end flex-wrap sm:!flex-nowrap">
          <ProgramAppointment
            v-if="program?.bookable && customer"
            :bookable="program.bookable"
            :programmable-id="program.programmable_id"
            :programmable-type="program.programmable_type"
            :customer="customer"
            @certified="patientStore.refreshCustomer()"
          />
          <BaseButton
            v-if="supportProgram?.has_flyer"
            density="comfortable"
            :loading="loadingFlyer"
            @click="downloadFlyer()"
          >
            <FileText class="size-4 mr-1" />
            Télécharger le document
          </BaseButton>
          <CustomerCommunicationDialog
            v-if="customer"
            :customer="customer"
            :program-id="program?.id"
            :on-certified="() => patientStore.refreshCustomer()"
          >
            <BaseButton density="comfortable">
              <Send class="size-4 mr-1" />
              SMS
            </BaseButton>
          </CustomerCommunicationDialog>
          <MSSanteButton
            v-if="consumer && informsDoctor"
            class="rounded-full h-8"
            :prefills="[mssantePrefill]"
            :consumer-key="consumerKey"
            :patient-name="patientName"
          >
            MSSanté
          </MSSanteButton>
          <CustomerProgramCertification
            v-if="program && customer"
            :customer="customer"
            @certified="patientStore.refreshCustomer()"
          />
        </div>
      </div>
    </div>

    <div v-if="supportProgram.addons?.length && customer" class="flex flex-col gap-4">
      <component
        :is="resolveAddon(addon.slug)"
        v-for="addon in supportProgram.addons"
        :key="addon.slug"
        :customer="customer"
        :consumer-key="$route.params.consumerKey as string"
        :support-program-id="program.programmable_id"
        :payload="addon.payload ?? {}"
      />
    </div>

    <div>
      <div class="flex flex-col gap-y-6">
        <div v-if="program.programmable?.pathway">
          <PathwayStepper
            :support-program="program.programmable"
            :consumer-key="$route.params.consumerKey as string"
            @skip-delay="skipDelay()"
            @start="start()"
          />
        </div>
        <div v-if="program.backings?.length && consumer && customer">
          <BackingDetails
            v-for="backing in program.backings"
            :key="backing.id"
            :backing="backing"
            :program-id="program.id"
            :attachments="program?.attachments"
            :consumer="consumer"
            :customer-id="customer.id"
          />
        </div>
        <SupportProgramAdvices
          v-if="supportProgram.advices?.length > 0"
          :advices="supportProgram.advices"
        />
        <SupportProgramStaticAdvices
          v-if="supportProgram.static_advices?.length > 0"
          :advices="supportProgram.static_advices"
        />
        <CustomerHealthJourney
          v-if="consumer && customer && program.bookable"
          :customer="customer"
          :current-program="program.id"
          :consumer="consumer"
        />
      </div>
    </div>
    <SupportProgramDocumentGrid
      v-if="program.attachments && program.attachments.length > 0"
      class="bg-transparent shadow-none"
      :attachments="program.attachments"
      :consumer="consumer"
    />
    <SupportProgramProtocol
      v-if="supportProgram.settings"
      :consumer-protocol="supportProgram.settings"
      :consumer="consumer"
    />
    <SupportProgramFooter
      :consumer-key="consumerKey"
      :program="program"
      @rejected="patientStore.refresh()"
      @delayed="patientStore.refresh()"
      @canceled="patientStore.refresh()"
    />
  </div>
</template>

<script lang="ts" setup>
import {computed, ComputedRef, ref, watch} from "vue";
import SupportProgramDescription from '@/program/components/support-program/SupportProgramDescription.vue';
import SupportProgramAdvices from '@/program/components/support-program/SupportProgramAdvices.vue';
import SupportProgramDocumentGrid from '@/program/components/support-program/SupportProgramDocumentGrid.vue';
import PathwayStepper from '@/pathway/components/PathwayStepper.vue';
import SupportProgramProtocol from '@/program/components/support-program/SupportProgramProtocol.vue';
import {usePatientStore} from '@/stores/patient.store';
import BackingDetails from "@/backing/components/BackingDetails.vue";
import pathwayService from "@/pathway/services/PathwayService";
import {ProgramSnapshot, SupportProgram, SupportProgramKind} from "@/program/interfaces/Program";
import {useRoute} from "vue-router";
import ProgramAppointment from "@/program/components/program-appointment/ProgramAppointment.vue";
import {storeToRefs} from "pinia";
import SupportProgramStaticAdvices from "@/program/components/support-program/SupportProgramStaticAdvices.vue";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import ProgramHook from "@/program/components/program-hook/ProgramHook.vue";
import ProgramHookPoll from "@/program/components/program-hook/ProgramHookPoll.vue";
import Consumer from "@/core/interfaces/consumers/Consumer";
import dayjs from "dayjs";
import HospitalProgramSalesCard from "@/program/components/hospital-program-sales/HospitalProgramSalesCard.vue";
import {isHospitalProgram} from "@/program/services/ProgramService";
import SupportProgramFooter from "@/program/components/support-program/SupportProgramFooter.vue";
import CustomerProgramKnowledgeables
    from "@/program/components/program-knowledgeables/CustomerProgramKnowledgeables.vue";
import CustomerCommunicationDialog from "@/customer/components/customer-communication/CustomerCommunicationDialog.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {FileText, Send} from '@lucide/vue';
import CustomerProgramOnboardingCommunication
    from "@/customer/components/customer-program-onboarding-communication/CustomerProgramOnboardingCommunication.vue";
import CustomerProgramCertification
    from "@/customer/components/customer-certification/CustomerProgramCertification.vue";
import CustomerHealthJourney from "@/customer/components/customer-health-journey/CustomerHealthJourney.vue";
import {supportProgramApi} from "@/container";
import {resolveAddon} from "@/program/services/SupportProgramAddonRegistry";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import MSSanteButton from "@/mssante/components/MSSanteButton.vue";
import {MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import usePatientMSSanteMessages from "@/mssante/composables/usePatientMSSanteMessages";
import {provideMSSanteSource} from "@/mssante/composables/useMSSanteSource";
import {useSystemStore} from "@/stores/system.store";

const patientStore = usePatientStore();
const {environment} = storeToRefs(useSystemStore());
const {customer} = storeToRefs(patientStore);
const route = useRoute();
const loadingFlyer = ref<boolean>(false);

// Every MSSanté message written on the page (addons included) is about this program.
provideMSSanteSource(() => ({type: 'program', id: parseInt(route.params.programId as string)}));

const refreshing = ref<boolean>(false);
const retried = ref<boolean>(false);

const consumer = computed(() => {
    return patientStore.getConsumer(route.params.consumerKey as string);
});

const program: ComputedRef<ProgramSnapshot> = computed(() => {
    const consumerKey = route.params.consumerKey as string;

    if (ConsumerKeyService.isBeneficiary(consumerKey)) {
        const beneficiary = customer.value?.beneficiaries.find(_beneficiary => _beneficiary.consumer_key === consumerKey);

        return beneficiary?.programs.find(p => p.id === parseInt(route.params.programId as string)) as ProgramSnapshot;
    } else {
        return customer.value?.programs.find(p => p.id === parseInt(route.params.programId as string)) as ProgramSnapshot;
    }
});

/**
 * If program not found in the customer store, try to refresh the customer data from the API to get the latest programs.
 * This can happen when a redirect to the program page happens and the store was already loaded with the customer.
 */
watch(program, (value) => {
    if (!value && !retried.value) {
        refreshing.value = true;
        patientStore.refresh()
            .finally(() => {
                retried.value = true;
                refreshing.value = false;
            });
    }

}, {immediate: true});

const supportProgram: ComputedRef<SupportProgram> = computed(() => {
    return program.value.programmable as SupportProgram;
});

const consumerKey: ComputedRef<string> = computed(() => {
    return route.params.consumerKey as string;
});

const {patientName, vaccinationMessage, programMessage} = usePatientMSSanteMessages(consumer);

/**
 * Whether the program concerns the patient's health: a veterinary program has no doctor to inform.
 */
const informsDoctor = computed((): boolean => supportProgram.value?.kind !== SupportProgramKind.VETERINARY);

/**
 * The message informing the patient's doctor, matching the program kind, signed by the
 * connected pharmacist. The page doesn't know when the vaccination happened, so its date is
 * left for the pharmacist to complete.
 */
const mssantePrefill = computed((): MSSantePrefill => {
    const signatory = environment.value?.user?.name ?? '';

    switch (supportProgram.value?.kind) {
        case SupportProgramKind.VACCINATION:
            return vaccinationMessage('[nom du vaccin]', [], signatory, false);
        default:
            return programMessage(program.value.name, signatory);
    }
});

const pollAnswered = ref(false);

/** The unanswered poll displayed in place of the program hook. */
const pendingPoll = computed(() => pollAnswered.value
    ? undefined
    : program.value?.polls?.find(poll => poll.display_in_program));

/** The consumer answering the poll, as expected by the poll actions. */
const pollConsumer = computed(() => ({
    id: customer.value?.id,
    key: consumerKey.value,
}) as Consumer);

watch(() => program.value?.id, () => {
    pollAnswered.value = false;
});

/**
 * The poll answer excluded the patient from the program: propagate the
 * masking to the program lists and reload the customer snapshot.
 */
const onPollHidden = () => {
    window.ipcRenderer.hideProgram(consumerKey.value, program.value.id);
    patientStore.refresh();
};

/**
 * Reload the pathway skipping the delay.
 */
const skipDelay = () => {
    return patientStore.loadPathway(
        program.value.programmable_id,
        route.params.consumerKey as string,
        true
    );
};

const start = () => {
    if (!program.value.programmable?.pathway?.next_step?.resolved?.action) {
        return;
    }

    const step = program.value.programmable.pathway.next_step;

    return pathwayService()
        .start(program.value.programmable.pathway, step, route.params.consumerKey as string);
};

const downloadFlyer = () => {
    if (!supportProgram.value.has_flyer) {
        return;
    }

    loadingFlyer.value = true;
    supportProgramApi().flyer(program.value.programmable_id, route.params.consumerKey as string)
        .then((result) => {
            const url = window.URL.createObjectURL(result);
            window.open(url);
        })
        .finally(() => loadingFlyer.value = false);
};
</script>
